/**
 * Kwota minimalna zamówienia z umowy ramowej — ostrzeżenie, nie blokada.
 *
 * Front liczy je NA ŻYWO w formularzu zapotrzebowania (nowe / edycja), gdzie backend
 * jeszcze nic nie wie o wpisywanych pozycjach. Reguły są lustrem
 * `apps.orders.min_order_amount` (wartość = Σ ilość × stawka po wszystkich pozycjach,
 * stawka wysokiego priorytetu przy priorytecie „high”; umowa = umowy wybranych pozycji,
 * a bez nich — pierwsza umowa podwykonawcy z ustawionym minimum). Zapisane
 * zapotrzebowanie dostaje tę samą informację z backendu (`min_order_amount_notice`).
 */

export interface MinOrderLineItemType {
  id: string;
  base_rate: string | null;
  high_priority_rate?: string | null;
}

export interface MinOrderAgreement {
  id: string;
  title: string;
  min_order_amount?: string | null;
  line_item_types: MinOrderLineItemType[];
}

/** Wiersz formularza pozycji — tylko pola potrzebne do policzenia wartości. */
export interface MinOrderLineDraft {
  is_custom?: boolean;
  line_item_type?: string | null;
  custom_unit_rate?: number | string | null;
  quantity?: number | string | null;
}

/** Kształt `min_order_amount_notice` z backendu (zapotrzebowanie i zamówienie). */
export interface MinOrderAmountNotice {
  agreement_id: string;
  agreement_title: string;
  min_order_amount: string;
  total_value: string;
  /** true = wartość poniżej minimum (ostrzeżenie); false = sama informacja o minimum. */
  below_minimum: boolean;
  shortfall: string;
}

const num = (v: number | string | null | undefined): number | null => {
  if (v == null || v === "") return null;
  const n = typeof v === "number" ? v : parseFloat(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

const money = (n: number) =>
  n.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Informacja o kwocie minimalnej dla pozycji wpisywanych w formularzu — obecna zawsze,
 * gdy umowa proponowanego podwykonawcy ma minimum (`below_minimum` mówi, czy to już
 * ostrzeżenie); null, gdy żadna umowa nie ma minimum. Pozycja bez znanej stawki
 * (np. ukryte stawki) liczy się jako 0 — tak samo jak backend.
 */
export function computeMinOrderNotice(
  agreements: MinOrderAgreement[] | undefined,
  lines: MinOrderLineDraft[] | undefined,
  priority: string | undefined,
): MinOrderAmountNotice | null {
  if (!agreements || agreements.length === 0) return null;
  const byLit = new Map<string, { agreement: MinOrderAgreement; lit: MinOrderLineItemType }>();
  for (const a of agreements) for (const lit of a.line_item_types ?? []) byLit.set(lit.id, { agreement: a, lit });

  let total = 0;
  const used = new Map<string, MinOrderAgreement>();
  for (const line of lines ?? []) {
    const qty = num(line.quantity) ?? 0;
    if (line.is_custom || !line.line_item_type) {
      total += (num(line.custom_unit_rate) ?? 0) * qty;
      continue;
    }
    const hit = byLit.get(line.line_item_type);
    if (!hit) continue;
    used.set(hit.agreement.id, hit.agreement);
    const high = priority === "high" ? num(hit.lit.high_priority_rate) : null;
    const rate = high ?? num(hit.lit.base_rate) ?? 0;
    total += rate * qty;
  }

  const candidates = used.size > 0
    ? [...used.values()]
    : agreements.filter((a) => num(a.min_order_amount) != null).slice(0, 1);

  let binding: { agreement: MinOrderAgreement; min: number } | null = null;
  for (const a of candidates) {
    const min = num(a.min_order_amount);
    if (min == null) continue;
    if (!binding || min > binding.min) binding = { agreement: a, min };
  }
  if (!binding) return null;
  const below = total < binding.min;
  return {
    agreement_id: binding.agreement.id,
    agreement_title: binding.agreement.title,
    min_order_amount: binding.min.toFixed(2),
    total_value: total.toFixed(2),
    below_minimum: below,
    shortfall: (below ? binding.min - total : 0).toFixed(2),
  };
}

/** Jedno zdanie — to samo w formularzu, szczegółach, oknie zatwierdzania i na zamówieniu. */
export function minOrderNoticeText(notice: MinOrderAmountNotice): string {
  const total = num(notice.total_value) ?? 0;
  const min = num(notice.min_order_amount) ?? 0;
  if (notice.below_minimum) {
    return (
      `Wartość zamówienia (${money(total)} PLN) jest niższa niż kwota minimalna zamówienia ` +
      `z umowy ramowej „${notice.agreement_title}” (${money(min)} PLN) — brakuje ${money(min - total)} PLN.`
    );
  }
  return (
    `Umowa ramowa „${notice.agreement_title}” ma kwotę minimalną zamówienia ${money(min)} PLN — ` +
    `wartość zamówienia (${money(total)} PLN) ją spełnia.`
  );
}
